import React, { useState, useEffect } from 'react';
import { FaShare, FaCopy, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next'; 
import api from '../../api/axiosConfig';
import { stringToColor } from '../../utils/avatarColor';

const ShareModal = ({ isOpen, onClose, property, onShareSuccess }) => {
  const { t } = useTranslation('ShareModal'); 

  const [copied, setCopied] = useState(false);
  const [sharers, setSharers] = useState([]);
  const [loadingSharers, setLoadingSharers] = useState(true);

  const shareUrl = property?.id 
    ? `${window.location.origin}/property/${property.id}` 
    : '';

  useEffect(() => {
    if (!isOpen || !property?.id) {
      setLoadingSharers(false);
      return;
    }

    const fetchSharers = async () => {
      try {
        const res = await api.get(`/properties/${property.id}/shares/`);
        setSharers(Array.isArray(res.data) ? res.data : (res.data.results || []));
      } catch (err) {
        console.warn('Failed to load sharers');
        setSharers([]);
      } finally {
        setLoadingSharers(false);
      }
    };

    fetchSharers();
  }, [isOpen, property?.id]);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert(t('shareModal.errors.copyFailed')); // ✅ Translated error
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share || !property?.title || !shareUrl) return;
    try {
      await navigator.share({ title: property.title, url: shareUrl });
      onShareSuccess?.();
      onClose();
    } catch (err) {
      if (err.name !== 'AbortError') {
        alert(t('shareModal.errors.shareFailed')); 
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center sm:items-center sm:justify-center"
      onClick={onClose}
    >
      <div 
        className="bg-[var(--bg-black)] w-full max-w-md rounded-t-2xl sm:rounded-2xl p-5 pt-4 text-[var(--text-black)] shadow-2xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-[var(--text-black)] flex items-center gap-2">
            <FaShare /> {t('shareModal.title')}
          </h3>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:text-pink-800 font-bold text-pink-500 bg-transparent border-none"
          >
            <FaTimes />
          </button>
        </div>

        {property?.id ? (
          <>
            {/* Share Link Input */}
            <div className="mb-4">
              <label className="text-xs text-[var(--text-black)] block mb-1">
                {t('shareModal.link')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 bg-[var(--bg-black)] text-[var(--text-black)] border border-[var(--border)] border-solid text-sm rounded px-3 py-2 focus:outline-none"
                />
                <button
                  onClick={handleCopy}
                  className={`px-3 py-2 rounded font-medium border-none flex items-center gap-1 ${
                    copied 
                      ? 'bg-green-500 text-white' 
                      : 'bg-pink-500 hover:bg-pink-600 text-white'
                  }`}
                >
                  {copied ? t('shareModal.copy.success') : <><FaCopy size={12} /> {t('shareModal.copy.button')}</>}
                </button>
              </div>
            </div>

            {/* Native Share */}
            {navigator.share && (
              <button
                onClick={handleNativeShare}
                className="w-full mb-3 py-3 bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] rounded-lg font-medium flex items-center justify-center gap-2"
              >
                <FaShare size={14} /> {t('shareModal.nativeShare')}
              </button>
            )}

            {/* Shared By */}
            {(sharers.length > 0 || loadingSharers) && (
              <div className="mt-4 pt-4 border-t border-gray-800">
                <h4 className="text-sm font-semibold text-gray-300 mb-2">
                  {t('shareModal.sharedBy.title', {
                    count: sharers.length,
                    person: sharers.length === 1 
                      ? t('shareModal.sharedBy.person') 
                      : t('shareModal.sharedBy.people')
                  })}
                </h4>
                {loadingSharers ? (
                  <div className="flex gap-2">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-gray-700 animate-pulse"></div>
                    ))}
                  </div>
                ) : (
                  <div className="flex gap-2 flex-wrap">
                    {sharers.slice(0, 8).map((sharer) => (
                      <div key={sharer.user?.id || sharer.id} className="relative group">
                        {sharer.user?.avatar && sharer.user.avatar.startsWith('http') ? (
                          <img
                            src={sharer.user.avatar}
                            alt={sharer.user.username || t('shareModal.user.fallback')}
                            className="w-8 h-8 rounded-full object-cover border-2 border-pink-500"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-pink-500 flex items-center justify-center text-white text-xs font-bold">
                            {(() => {
                              const displayName = sharer.user?.username || 'U';
                              const initials = displayName.charAt(0).toUpperCase();
                              const bgColor = stringToColor(sharer.user?.username || sharer.user?.id || 'user');
                              return (
                                <div 
                                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                  style={{ backgroundColor: bgColor }}
                                >
                                  {initials}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 hidden group-hover:block bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          {sharer.user?.username || t('shareModal.user.fallback')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-white">
            {t('shareModal.errors.invalidProperty')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareModal;